import { Component, Input, forwardRef, Output, EventEmitter, HostBinding, OnInit, SimpleChanges, OnChanges, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BbSelectService } from '../../../services/bb-select.service';

@Component({
	selector: 'bb-select',
	templateUrl: './bb-select.component.html',
	styleUrls: ['./bb-select.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbSelectComponent),
		multi: true
	}]
})

export class BbSelectComponent implements ControlValueAccessor, OnInit, OnChanges {

	@Input() preset: string;
	@Input() options: BbSelectOption[] = [];
	@Input() theme = 'default';
	@Input() defaultValue: string;
	@Input() placeholder: string;
	@Input() disabled = false;
	@Output() optionSelected = new EventEmitter();
	@HostBinding('class') get hostClass() {
		const classStr = (this.theme ? `theme-${this.theme}` : '');
		return classStr;
	};

	propagateChange: any;
	value: string;
	showCustomDropdownExpanded = false;
	displayValue: string;

	constructor(
		private bbSelectService: BbSelectService,
		protected hostElement: ElementRef
	) { }

	ngOnInit() {
		if (this.preset) {
			this.bbSelectService.getOptions(this.preset).subscribe((options: BbSelectOption[]) => {
				this.options = options;
				this.getDefaultValue();
			});
		} else {
			this.getDefaultValue();
		}
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges?.options?.currentValue && ! simpleChanges?.options?.firstChange) {
			this.getDefaultValue();
		}
	}

	getDefaultValue() {
		if (this.value === undefined) {
			if (this.defaultValue !== undefined) {
				this.value = this.defaultValue;
			} else if (this.options && this.options.length) {
				for (let i = 0; i < this.options.length; i++) {
					const option: BbSelectOption = this.options[i];
					if (option.default) {
						this.value = option.value;
						this.defaultValue = option.value;
						break;
					}
				}
				if (this.value === undefined) {
					this.value = this.options[0].value;
				}
			}
		}

		this.setDisplayLabel();
	}

	valueChanged(value: string) {
		this.value = value;
		if (this.propagateChange) {
			this.propagateChange(value);
		}
		if (this.options && this.options.length) {
			this.optionSelected.emit(this.options.find((option) => option.value === value));
		} else {
			this.optionSelected.emit(value);
		}
		this.setDisplayLabel();
	}

	optionTrackBy(index: number, option: BbSelectOption) {
		return option?.value || index;
	}

	writeValue(value: string) {
		if (value === this.value) {
			return;
		}
		if (this.defaultValue === undefined) {
			this.value = value;
		} else if (typeof value !== 'undefined') {
			this.value = value;
		} else {
			this.value = this.defaultValue;
		}
		this.setDisplayLabel();
	}

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.hostElement.nativeElement.style.pointerEvents = 'none';
			this.hostElement.nativeElement.classList.add('disabled');
		} else {
			this.hostElement.nativeElement.style.pointerEvents = 'all';
			this.hostElement.nativeElement.classList.remove('disabled');
		}
	}

	// for custom dropdown display label instead of value as 'placeholder'
	private setDisplayLabel(): void {
		if (!this.value) {
			this.displayValue = '';
			return;
		}
		const activeValue = this.options?.find((opt => opt.value === this.value));

		this.displayValue = activeValue ? activeValue.label : this.value;
	}
}

export interface BbSelectOption {
	value: string;
	label: string;
	default?: boolean;
	disabled?: boolean;
}
